<template>
    <div class="flex items-center justify-between mb-8">
        <Head :title="$t('agencies.clearTitle')" />
        <page-title :title="$t('agencies.clearTitle')"></page-title>
    </div>

    <div class="bg-white shadow overflow-hidden sm:rounded-md p-4">
        <div class="mb-4 flex flex-col md:flex-row justify-between w-full space-y-3 md:space-y-0 text-right">
            <input 
                type="text" 
                v-model="search" 
                :placeholder="$t('agencies.search')" 
                autocomplete="off"
                class="shadow-sm focus:ring-minplan-blue focus:border-minplan-blue block w-full sm:text-sm border-gray-300 rounded-md md:w-1/3 text-left"
            />
            <div class="flex space-x-2 w-full justify-end">
                <button 
                    v-if="mysqlDumpPath"
                    type="button"
                    class="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none uppercase"
                    @click="downloadBackup"
                >
                    <span class="hidden md:inline font-bold">Download DB Backup</span>
                </button>
                <button 
                    type="button"
                    :disabled="selectedAgencies.length === 0"
                    class="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none uppercase disabled:opacity-50"
                    @click="confirmDelete"
                >
                    <TrashIcon class="-ml-1 md:mr-2 h-5 w-5 text-white" />
                    <span class="hidden md:inline font-bold">{{ $t("agencies.deleteSelected") }}</span>
                </button>
            </div>
        </div>

        <div class="mb-4 flex flex-col md:flex-row shadow overflow-hidden sm:rounded-md p-4 space-y-3 md:space-y-0" v-if="agencies.length > 0">
            <div class="flex items-center">
                <input 
                    type="checkbox" 
                    id="selectAll"
                    :checked="allSelected"
                    @change="toggleSelectAll"
                    class="border-gray-300 rounded mr-4 cursor-pointer"
                />
                <label for="selectAll" class="text-sm font-medium text-gray-700 cursor-pointer">
                    Select All
                </label>
            </div>
        </div>

        <ul role="list" class="divide-y divide-gray-200 border-t border-gray-200 mt-4">
            <li v-for="agency in agencies" :key="agency.uuid" class="px-4 py-4 sm:px-6 flex items-center justify-between hover:bg-minplan-lightgreybeige cursor-pointer" @click="toggleSelection(agency.uuid)">
                <div class="flex items-center">
                    <input 
                        type="checkbox" 
                        :value="agency.uuid" 
                        v-model="selectedAgencies"
                        @click.stop
                        class="border-gray-300 rounded mr-4"
                    />
                    <div class="text-sm font-medium text-minplan-blue truncate">
                        {{ agency.name }}
                        <span class="text-gray-500 text-xs ml-1" v-if="agency.created_at || agency.advisor_name">
                            - {{ agency.created_at }} <span v-if="agency.advisor_name">({{ agency.advisor_name }})</span>
                        </span>
                        <span v-if="agency.deactivated" class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {{ $t('agencies.deactivated') }}
                        </span>
                    </div>
                </div>
            </li>
            <li v-if="agencies.length === 0" class="px-4 py-4 sm:px-6 text-center text-sm text-gray-500">
                Keine Agenturen gefunden
            </li>
        </ul>
    </div>
</template>

<script>
import MainLayout from "@/Layouts/MainLayout";
import PageTitle from "@/Shared/Components/PageTitle";
import { TrashIcon } from "@heroicons/vue/outline";
import SettingsLayout from "@/Layouts/SettingsLayout";
import { Head } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import debounce from "lodash/debounce";

export default {
    layout: [MainLayout, SettingsLayout],
    props: ['agencies', 'filters', 'mysqlDumpPath'],
    components: {
        Head,
        PageTitle,
        TrashIcon
    },
    data() {
        return {
            search: this.filters?.search || '',
            selectedAgencies: []
        };
    },
    watch: {
        search: debounce(function(value) {
            Inertia.get(this.route('admin.agencies.clear'), { search: value }, {
                preserveState: true,
                replace: true
            });
        }, 300)
    },
    computed: {
        allSelected() {
            return this.agencies.length > 0 && this.agencies.every(agency => this.selectedAgencies.includes(agency.uuid));
        }
    },
    methods: {
        toggleSelection(uuid) {
            const index = this.selectedAgencies.indexOf(uuid);
            if (index === -1) {
                this.selectedAgencies.push(uuid);
            } else {
                this.selectedAgencies.splice(index, 1);
            }
        },
        toggleSelectAll() {
            if (this.allSelected) {
                this.agencies.forEach(agency => {
                    const index = this.selectedAgencies.indexOf(agency.uuid);
                    if (index !== -1) {
                        this.selectedAgencies.splice(index, 1);
                    }
                });
            } else {
                this.agencies.forEach(agency => {
                    if (!this.selectedAgencies.includes(agency.uuid)) {
                        this.selectedAgencies.push(agency.uuid);
                    }
                });
            }
        },
        async ensureSwal() {
            if (window.Swal) return window.Swal;
            await new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/sweetalert2@11';
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
            return window.Swal;
        },
        downloadBackup() {
            this.ensureSwal().then(() => {
                window.Swal.fire({
                    title: 'Download Database Backup',
                    text: 'Please enter the database password:',
                    input: 'password',
                    inputAttributes: {
                        autocapitalize: 'off',
                        autocomplete: 'new-password'
                    },
                    showCancelButton: true,
                    confirmButtonText: 'Download',
                    showLoaderOnConfirm: true,
                    preConfirm: (password) => {
                        return axios.post(this.route('admin.agencies.download.backup'), { password: password }, { responseType: 'blob' })
                            .then(response => {
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                const contentDisposition = response.headers['content-disposition'];
                                let fileName = 'backup.sql';
                                if (contentDisposition) {
                                    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                                    if (fileNameMatch.length === 2)
                                        fileName = fileNameMatch[1];
                                }
                                link.setAttribute('download', fileName);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(url);
                            })
                            .catch(error => {
                                let errorMessage = 'Download failed.';
                                if (error.response && error.response.data && error.response.data.errors && error.response.data.errors.password) {
                                    errorMessage = error.response.data.errors.password[0];
                                }
                                window.Swal.showValidationMessage(errorMessage);
                            });
                    },
                    allowOutsideClick: () => !window.Swal.isLoading()
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.$notify({
                            group: "success",
                            title: "actions.success",
                            text: "Download completed"
                        });
                    }
                });
            });
        },
        confirmDelete() {
            this.ensureSwal().then(() => {
                window.Swal.fire({
                    title: this.$t('agencies.confirmDeleteSelected'),
                    text: 'Please enter the cleanup password:',
                    input: 'password',
                    inputAttributes: {
                        autocapitalize: 'off',
                        autocomplete: 'new-password'
                    },
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3085d6',
                    confirmButtonText: this.$t('actions.delete') || 'Delete',
                    cancelButtonText: this.$t('actions.cancel') || 'Cancel',
                    showLoaderOnConfirm: true,
                    preConfirm: (password) => {
                        if (!password) {
                            window.Swal.showValidationMessage('Password is required');
                            return false;
                        }
                        return new Promise((resolve, reject) => {
                            Inertia.delete(this.route('admin.agencies.clear.destroy'), {
                                data: { 
                                    uuids: this.selectedAgencies,
                                    password: password
                                },
                                onSuccess: () => {
                                    this.selectedAgencies = [];
                                    this.$notify({
                                        group: "success",
                                        title: "actions.success",
                                        text: "agencies.deleted"
                                    });
                                    resolve();
                                },
                                onError: (errors) => {
                                    if (errors.password) {
                                        window.Swal.showValidationMessage(errors.password);
                                    } else {
                                        window.Swal.showValidationMessage('An error occurred during deletion.');
                                    }
                                    resolve(false);
                                }
                            });
                        });
                    },
                    allowOutsideClick: () => !window.Swal.isLoading()
                });
            });
        }
    }
};
</script>
